import { cleanProps } from '@lib/utils';
import { take } from 'lodash';

export interface NamedPoints<T = any> {
    top: T;
    right: T;
    bottom: T;
    left: T;
}

/**
 * Create internal props & clean method for css mixins
 * @param func
 * @param props
 * @returns {*}
 */
export const createCleaner = (func: any, props: string[]) => {
    func.internalProps = props;
    func.clean = (rest) => cleanProps(rest, props);
    return func;
};

/**
 * Create Points from an array
 * @example
 * createPoints() // [0, 0, 0, 0]
 * createPoints([]) // [0, 0, 0, 0]
 * createPoints([1]) // [1, 1, 1, 1]
 * createPoints([1, 2]) // [1, 2, 1, 2]
 * createPoints([1, 2, 3]) // [1, 2, 3, 2]
 * createPoints([1, 2, 3, 4]) // [1, 2, 3, 4]
 *
 * @param points
 * @returns {T[]}
 */
export const createPoints = <T = number>(points: T[] = []): T[] => {
    const points$ = take(points, 4);

    switch (points.length) {
        case 1:
            // createPoints([1]) // [1, 1, 1, 1]
            return new Array(4).fill(points$[0]);

        case 2:
            // createPoints([1, 2]) // [1, 2, 1, 2]
            return points$.concat(points$);

        case 3:
            // createPoints([1, 2, 3]) // [1, 2, 3, 2]
            return [...points$, points$[1]];

        case 4:
            // createPoints([1, 2, 3, 4]) // [1, 2, 3, 4]
            return points$;

        default:
            // createPoints() // [0, 0, 0, 0]
            return createPoints<number>([0]) as any;
    }
};

/**
 * @param points
 * @returns {{top: T, left: T, bottom: T, right: T}}
 */
export const createNamedPoints = <T = number>(points?: T[]): NamedPoints<T> => {
    const [top, right, bottom, left] = createPoints<T>(points);
    return { top, right, bottom, left };
};
